import React from 'react';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { formatMoney, getFullAddress, renderNumber, formatDateCheckInOut } from 'src/utils/index';
import { RootState } from 'src/store';
import {
  IReviewBookState,
  IMenuState,
  setCoupon,
  setRedeemCode,
  setUpdateCoupon,
} from 'src/store/slices/index';
import { IRedeemCode, ITaxesAndFees } from 'src/common-types/index';
import { ClockSvg, NoImageSvg } from 'src/assets/index';
import { HotelStars, PriceSkeleton } from 'src/components/components/index';
import { DATE_FORMAT_CHECK_IN_OUT_YEAR } from 'src/constants/index';
import { DiscountCode } from '../discount-code';
import isEmpty from 'lodash/isEmpty';
import isUndefined from 'lodash/isUndefined';
import './style.scss';

interface IMapStateToProps {
  reviewBookStore: IReviewBookState;
  navigationMenuStore: IMenuState;
}

interface IMapDispatchToProps {
  setCoupon: (coupon: string) => void;
  setUpdateCoupon: (coupon: boolean) => void;
  setRedeemCode: (coupon: IRedeemCode) => void;
}

interface IProps extends IMapStateToProps, IMapDispatchToProps {}

const ZERO = 0;

class RoomInfoComponent extends React.Component<IProps, null> {
  componentDidMount() {
    this.props.setCoupon('');
    this.props.setRedeemCode(null);
    this.props.setUpdateCoupon(false);
  }

  getFeeNode = (fee: ITaxesAndFees, index: number): React.ReactNode => {
    const isNotZeroFee = fee.value > ZERO;

    return (
      <div className="room-info__fee" key={index}>
        <p className="room-info__fee__price">
          {isNotZeroFee ? fee.title : fee.remarks}{' '}
          <span className="room-info__fee__price__value">
            {fee.isApproximateValue ? <FormattedMessage id="fee.approx" /> : null}{' '}
            {fee.formattedValue}
          </span>
        </p>
        {isNotZeroFee ? (
          <>
            {!isUndefined(fee.unitType) && (
              <p>
                <FormattedMessage id="fee.unit.type" />: {fee.unitType}
              </p>
            )}
            {!isUndefined(fee.frequencyType) && (
              <p>
                <FormattedMessage id="fee.frequency.type" />: {fee.frequencyType}
              </p>
            )}
            {!isUndefined(fee.remarks) && (
              <p>
                <FormattedMessage id="fee.remarks" />: {fee.remarks}
              </p>
            )}
          </>
        ) : null}
      </div>
    );
  };

  getPayAtPropertyFeesNode = (fees: ITaxesAndFees[]): React.ReactNode => {
    return (
      <>
        <div className="room-info__pay-info">
          <p className="room-info__pay-info-fee-text">
            <FormattedMessage id="pay.property" />:
          </p>
        </div>
        <div className="room-info__fee-wrapper">{fees.map(this.getFeeNode)}</div>
      </>
    );
  };

  getOptionalFeesNode = (fees: ITaxesAndFees[]): React.ReactNode => {
    return (
      <>
        <div className="room-info__pay-info">
          <p className="room-info__pay-info-fee-text">
            <FormattedMessage id="other.optional.fees" />:
          </p>
        </div>
        <div className="room-info__fee-wrapper">{fees.map(this.getFeeNode)}</div>
      </>
    );
  };

  render(): React.ReactNode {
    const { bookingComplete, loadingBooking, isUpdatePrice, isBookingInProgress, bookingSummary } =
      this.props.reviewBookStore;
    const { items } = this.props.navigationMenuStore;
    const {
      name,
      address,
      roomsCount,
      roomsTitle,
      roomDescription,
      checkIn,
      checkOut,
      nightsCount,
      bookingPrice,
      titleImage,
      stars,
      city,
      countryCode,
      countryName,
      zipCode,
      state,
      balance,
    } = bookingSummary.bookingCard;
    const { totalTaxes, payNowPrice, payAtPropertyFees, optionalFees, totalFarePrice, savings } =
      bookingPrice;
    const isNotification = bookingComplete || loadingBooking || isBookingInProgress;
    const mealBasisInfo = bookingSummary.bookingCard.package.rooms[ZERO].roomBasis;
    const mealBasisPrice = bookingSummary.bookingCard.package.priceDifference;
    const pricePerNight = bookingSummary.bookingCard.package.pricePerNight;

    const isPayAtPropertyFeesSpecified = !isEmpty(payAtPropertyFees);
    const isOptionalFeesSpecified = !isEmpty(optionalFees);
    const isAnyApproxValueSpecified =
      (isPayAtPropertyFeesSpecified &&
        !isUndefined(payAtPropertyFees.find((f) => f.isApproximateValue))) ||
      (isOptionalFeesSpecified && !isUndefined(optionalFees.find((t) => t.isApproximateValue)));

    return (
      <div className="room-info">
        <div className="room-info__wrapper">
          {bookingComplete ? (
            <div className="room-info__label-info">
              <p className="room-info__label-text-per-night">
                <FormattedMessage id="avg.rate" />
              </p>
              <p className="room-info__label-price">${renderNumber(pricePerNight)}</p>
              <p className="room-info__label-text-per-night">
                <FormattedMessage id="per.night" />
              </p>
            </div>
          ) : null}
          {titleImage.length !== ZERO ? (
            <div className="room-info__room-image">
              <img className="room-info__room-img" src={titleImage} alt={roomsTitle} />
            </div>
          ) : (
            <div className="room-info__room-img no-image">
              <NoImageSvg />
            </div>
          )}
          <div className="room-info__content border-bm">
            <p className="room-info__hotel-name">{name}</p>
            <div className="hotel-info__stars">
              <HotelStars stars={stars} />
            </div>
            <p className="room-info__address">
              {getFullAddress(address, city, state, zipCode, countryCode, countryName).join(', ')}
            </p>
            <div className="room-info__room-info-block">
              <p className="room-info__room-info-title">{roomsTitle}</p>
              <p className="room-info__room-info-description">{roomDescription}</p>
            </div>
            <div className="room-info__period">
              <div className="room-info__period-item">
                <p className="room-info__period-item-title">
                  <FormattedMessage id="check.in" />:
                </p>
                <p className="room-info__period-item-description">
                  {formatDateCheckInOut(checkIn, DATE_FORMAT_CHECK_IN_OUT_YEAR)}
                </p>
              </div>
              <div className="room-info__period-item">
                <p className="room-info__period-item-title">
                  <FormattedMessage id="check.out" />:
                </p>
                <p className="room-info__period-item-description">
                  {formatDateCheckInOut(checkOut, DATE_FORMAT_CHECK_IN_OUT_YEAR)}
                </p>
              </div>
            </div>
          </div>
          {!(loadingBooking || isBookingInProgress) && (
            <>
              <div className="room-info__content pb-5">
                <h4 className="room-info__price-title">
                  <FormattedMessage id="price.details" />
                </h4>
                {isUpdatePrice && <PriceSkeleton />}
                {!isUpdatePrice && (
                  <>
                    <div className="room-info__pay-info">
                      <p className="room-info__pay-info-text">
                        <FormattedMessage id="avg.nightly.rate" /> ${renderNumber(pricePerNight)}{' '}
                        <FormattedMessage id="per.night" />
                      </p>
                    </div>
                    <div className="room-info__price-info">
                      <p className="room-info__price-info-text">
                        <FormattedMessage id="public.price.details" /> {roomsCount}{' '}
                        <FormattedMessage id="room" values={{ count: roomsCount }} /> x{' '}
                        <FormattedMessage id="nights" values={{ count: nightsCount }} />
                      </p>
                      <p className="room-info__price-info-text">
                        {formatMoney.format(totalFarePrice)}
                      </p>
                    </div>
                    {mealBasisPrice !== ZERO ? (
                      <div className="room-info__price-info pt-0">
                        <p className="room-info__price-meal-basis-text">
                          <FormattedMessage id="including" /> {mealBasisInfo}
                          {formatMoney.format(mealBasisPrice)}
                        </p>
                      </div>
                    ) : null}
                    {savings ? (
                      <>
                        <div className="room-info__price-info">
                          <p className="room-info__price-savings-text">
                            <FormattedMessage
                              id={items?.promo ? 'savings' : 'member.savings.label'}
                            />
                          </p>
                          <p className="room-info__price-savings-text">
                            -{formatMoney.format(savings)}
                          </p>
                        </div>
                      </>
                    ) : null}
                    {!!balance && items?.pointsSettings.hotel.isRemainingBalanceEnabled && (
                      <div className="room-info__price-credits">
                        <p className="room-info__price-credits-text">
                          <FormattedMessage id="leisure.credits.balance" /> {balance}
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>
              {!isUpdatePrice && (
                <>
                  <div className="room-info__content border-bm">
                    {!items?.promo && <DiscountCode />}
                  </div>
                  <div className="room-info__content">
                    <div className="room-info__total-price-info-wrapper">
                      <div className="room-info__total-price-info">
                        <p className="room-info__price-info-text-total">
                          <FormattedMessage id="total.pay" />:
                        </p>
                        {totalTaxes !== ZERO ? (
                          <p className="room-info__note">
                            <FormattedMessage id="including.taxes.fees" />{' '}
                            {formatMoney.format(totalTaxes)}
                          </p>
                        ) : (
                          <p className="room-info__note">
                            <FormattedMessage id="zero.charge.for.taxes.fees" />
                          </p>
                        )}
                      </div>
                      <div className="room-info__price-info-amount-wrapper">
                        {!bookingComplete ? (
                          <p className="room-info__public-price-amount">
                            {formatMoney.format(totalFarePrice)}
                          </p>
                        ) : null}
                        <p className="room-info__price-info-amount">
                          {formatMoney.format(payNowPrice)}
                        </p>
                      </div>
                    </div>
                    {isPayAtPropertyFeesSpecified &&
                      this.getPayAtPropertyFeesNode(payAtPropertyFees)}
                    {isOptionalFeesSpecified && this.getOptionalFeesNode(optionalFees)}
                    {isAnyApproxValueSpecified && (
                      <div className="room-info__fee-wrapper">
                        <div className="room-info__note">
                          <FormattedMessage id="fee.instruction" />
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </div>
        {!isNotification && (
          <div className="room-info__label">
            <ClockSvg />
            <p className="room-info__label-text">
              <FormattedMessage id="act.fast" />
            </p>
          </div>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state: RootState): IMapStateToProps => {
  return {
    reviewBookStore: state.reviewBookStore,
    navigationMenuStore: state.navigationMenuStore,
  };
};

const mapDispatchToProps: IMapDispatchToProps = {
  setCoupon,
  setRedeemCode,
  setUpdateCoupon,
};

export const RoomInfo = connect(mapStateToProps, mapDispatchToProps)(RoomInfoComponent);

